/**
 * Codec for the `attribution` cookie, written by the proxy (`src/proxy.ts`) and
 * read during render (`app/[lang]/(routes)/layout.tsx`) and in the browser
 * (`lib/analytics/PosthogAnalytics.ts`).
 *
 * The value must survive three readers that percent-decode it a *different*
 * number of times:
 *
 * - Same request: Next forwards middleware cookies to the render via the
 *   `x-middleware-set-cookie` header. `parseSetCookie` splits the header (decode
 *   #1, guarded) and then runs `decodeURIComponent` on the already-decoded value
 *   (decode #2, unguarded).
 * - Next request: the browser echoes the cookie back and `RequestCookies` decodes
 *   it exactly once, inside a try/catch.
 * - Browser: `document.cookie` returns the serialized value, decoded zero times.
 *
 * Storing raw JSON therefore threw `URIError: URI malformed` out of the
 * `cookies()` accessor — before any page code runs, so the whole request 500s —
 * for any attribution param containing a bare `%` (WEBAPP-11P), and silently
 * mangled valid escapes (`%20` reaching the render as a space). No
 * percent-encoding scheme inverts both readers; base64url contains no `%`, so
 * the extra decode pass is a no-op and both agree.
 */

const toBase64Url = (base64: string) =>
  base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const fromBase64Url = (value: string) =>
  value.replace(/-/g, "+").replace(/_/g, "/");

const asParams = (parsed: unknown): Record<string, string> | null =>
  typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
    ? (parsed as Record<string, string>)
    : null;

/** Serializes attribution params for storage in the `attribution` cookie. */
export const encodeAttribution = (params: Record<string, string>): string => {
  const bytes = new TextEncoder().encode(JSON.stringify(params));
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return toBase64Url(btoa(binary));
};

/**
 * Reads an `attribution` cookie value. Never throws: an unreadable cookie means
 * we lose attribution for that visit, which must not take the page down with it.
 */
export const decodeAttribution = (raw: string): Record<string, string> => {
  try {
    const binary = atob(fromBase64Url(raw));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const params = asParams(JSON.parse(new TextDecoder().decode(bytes)));
    if (params) return params;
  } catch {
    // Not base64url — fall through to the legacy plain-JSON format below.
  }

  // Cookies written before the base64url switch hold plain JSON. They stay
  // readable for the remainder of their 30-day life.
  try {
    return asParams(JSON.parse(raw)) ?? {};
  } catch {
    return {};
  }
};

const COOKIE_PREFIX = "attribution=";

/**
 * Reads the `attribution` cookie out of a `document.cookie` string. The browser
 * hands back what was serialized, so legacy plain-JSON values still arrive
 * percent-encoded; base64url has no `%`, making that decode a no-op.
 */
export const readAttributionCookie = (
  documentCookie: string,
): Record<string, string> => {
  const raw = documentCookie
    .split("; ")
    .find((cookie) => cookie.startsWith(COOKIE_PREFIX))
    ?.slice(COOKIE_PREFIX.length);

  if (!raw) return {};

  try {
    return decodeAttribution(decodeURIComponent(raw));
  } catch {
    // `decodeURIComponent` throws on a malformed escape; the value is still
    // worth a decode attempt as-is.
    return decodeAttribution(raw);
  }
};
